/** Shared portfolio filters for Risk Overview ↔ Intelligence View (#46). */

export type RiskFilter = 'all' | 'high' | 'medium' | 'low' | 'unscored'
export type SingleSourceFilter = 'all' | 'yes' | 'no'
/** Default Direct so Indirect load does not dilute product-tied KPIs. */
export type ProcurementTypeFilter = 'all' | 'direct' | 'indirect'

export type EsPortfolioFilters = {
  risk: RiskFilter
  category: string
  subCategory: string
  brand: string
  region: string
  /** Country / plant location (same field as Intelligence "Country"). */
  location: string
  singleSource: SingleSourceFilter
  procurementType: ProcurementTypeFilter
}

export const DEFAULT_PORTFOLIO_FILTERS: EsPortfolioFilters = {
  risk: 'all',
  category: '',
  subCategory: '',
  brand: '',
  region: '',
  location: '',
  singleSource: 'all',
  procurementType: 'direct',
}

const STORAGE_KEY = 'es.portfolioFilters.v2'

/** Query keys written to the URL (and preserved on Overview ↔ Intelligence nav). */
export const PORTFOLIO_QUERY_KEYS = [
  'risk',
  'category',
  'subCategory',
  'brand',
  'region',
  'location',
  'singleSource',
  'procurementType',
  'supplier',
] as const

export function isRiskFilter(v: string): v is RiskFilter {
  return v === 'all' || v === 'high' || v === 'medium' || v === 'low' || v === 'unscored'
}

export function isSingleSourceFilter(v: string): v is SingleSourceFilter {
  return v === 'all' || v === 'yes' || v === 'no'
}

export function isProcurementTypeFilter(v: string): v is ProcurementTypeFilter {
  return v === 'all' || v === 'direct' || v === 'indirect'
}

export function loadPortfolioFiltersFromStorage(): EsPortfolioFilters {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_PORTFOLIO_FILTERS }
    const parsed = JSON.parse(raw) as Partial<EsPortfolioFilters>
    return normalizePortfolioFilters(parsed)
  } catch {
    return { ...DEFAULT_PORTFOLIO_FILTERS }
  }
}

export function savePortfolioFiltersToStorage(filters: EsPortfolioFilters): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
  } catch {
    // ignore quota / private mode
  }
}

export function normalizePortfolioFilters(
  partial: Partial<EsPortfolioFilters> | null | undefined
): EsPortfolioFilters {
  const risk =
    partial?.risk && isRiskFilter(partial.risk) ? partial.risk : DEFAULT_PORTFOLIO_FILTERS.risk
  const singleSource =
    partial?.singleSource && isSingleSourceFilter(partial.singleSource)
      ? partial.singleSource
      : DEFAULT_PORTFOLIO_FILTERS.singleSource
  const procurementType =
    partial?.procurementType && isProcurementTypeFilter(partial.procurementType)
      ? partial.procurementType
      : DEFAULT_PORTFOLIO_FILTERS.procurementType
  return {
    risk,
    category: (partial?.category || '').trim(),
    subCategory: (partial?.subCategory || '').trim(),
    brand: (partial?.brand || '').trim(),
    region: (partial?.region || '').trim(),
    location: (partial?.location || '').trim(),
    singleSource,
    procurementType,
  }
}

export function parsePortfolioFiltersFromSearch(
  params: URLSearchParams
): Partial<EsPortfolioFilters> {
  const out: Partial<EsPortfolioFilters> = {}
  const risk = (params.get('risk') || '').toLowerCase()
  if (isRiskFilter(risk)) out.risk = risk
  const category = params.get('category')
  if (category != null && category !== '') out.category = category
  const subCategory = params.get('subCategory')
  if (subCategory != null && subCategory !== '') out.subCategory = subCategory
  const brand = params.get('brand')
  if (brand != null && brand !== '') out.brand = brand
  const region = params.get('region')
  if (region != null && region !== '') out.region = region
  // Accept both location and country aliases
  const location = params.get('location') || params.get('country')
  if (location != null && location !== '') out.location = location
  const singleSource = (params.get('singleSource') || '').toLowerCase()
  if (isSingleSourceFilter(singleSource)) out.singleSource = singleSource
  const procurementType = (params.get('procurementType') || '').toLowerCase()
  if (isProcurementTypeFilter(procurementType)) out.procurementType = procurementType
  return out
}

export function portfolioFiltersToSearchParams(
  filters: EsPortfolioFilters,
  extra?: { supplier?: string | null }
): URLSearchParams {
  const p = new URLSearchParams()
  if (filters.risk !== 'all') p.set('risk', filters.risk)
  if (filters.category) p.set('category', filters.category)
  if (filters.subCategory) p.set('subCategory', filters.subCategory)
  if (filters.brand) p.set('brand', filters.brand)
  if (filters.region) p.set('region', filters.region)
  if (filters.location) p.set('location', filters.location)
  if (filters.singleSource !== 'all') p.set('singleSource', filters.singleSource)
  // Persist Direct default in URL only when not default? Keep explicit for shareability when non-direct.
  if (filters.procurementType !== 'direct') p.set('procurementType', filters.procurementType)
  if (extra?.supplier) p.set('supplier', extra.supplier)
  return p
}

export function portfolioSearchString(
  filters: EsPortfolioFilters,
  extra?: { supplier?: string | null }
): string {
  const p = portfolioFiltersToSearchParams(filters, extra)
  const s = p.toString()
  return s ? `?${s}` : ''
}

/** Path helper for Overview KPI / supplier deep-links into Intelligence. */
export function intelligencePath(
  filters: EsPortfolioFilters,
  extra?: { supplier?: string | null; risk?: RiskFilter }
): string {
  const merged = extra?.risk ? { ...filters, risk: extra.risk } : filters
  return `/early-signals/risk-matrix${portfolioSearchString(merged, {
    supplier: extra?.supplier,
  })}`
}

export function overviewPath(filters: EsPortfolioFilters): string {
  return `/early-signals/executive-summary${portfolioSearchString(filters)}`
}

/** Nav helper: attach stored portfolio params for Overview / Intelligence tabs. */
export function portfolioQueryForNav(): string {
  return portfolioSearchString(loadPortfolioFiltersFromStorage())
}

export function hasActivePortfolioFilters(filters: EsPortfolioFilters): boolean {
  return (
    filters.risk !== 'all' ||
    !!filters.category ||
    !!filters.subCategory ||
    !!filters.brand ||
    !!filters.region ||
    !!filters.location ||
    filters.singleSource !== 'all' ||
    filters.procurementType !== 'direct'
  )
}

/** Match supplier against Direct/Indirect filter (null/unknown treated as Direct for legacy rows). */
export function matchesProcurementTypeFilter(
  procurementType: string | null | undefined,
  filter: ProcurementTypeFilter
): boolean {
  if (filter === 'all') return true
  const pt = procurementType === 'indirect' ? 'indirect' : 'direct'
  return pt === filter
}
